import type { ComboboxOption } from "@v1/components/designSystem/combobox/ComboboxContext";
import type { ConstructedAnalyticsLink } from "schemas/generated/analyticsLink";
import type { Experiment } from "schemas/generated/experiment";

import * as React from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { Spinner } from "@replo/design-system/components/spinner/Spinner";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import twMerge from "@replo/design-system/utils/twMerge";
import { skipToken } from "@tanstack/react-query";
import Input from "@v1/components/common/designSystem/Input";
import { successToast } from "@v1/components/designSystem/alert/Toast";
import DeprecatedButton from "@v1/components/designSystem/button/Button";
import { Combobox } from "@v1/components/designSystem/combobox/Combobox";
import { useCreatableComboboxInputOptions } from "@v1/components/designSystem/combobox/hooks/useCreatableComboboxInputOptions";
import { Expandable } from "@v1/components/designSystem/expandable/Expandable";
import { MenuItem } from "@v1/components/designSystem/menu/MenuItem";
import { DetailsContainer } from "@v1/features/experiments/components/DetailsContainer";
import { useExperimentEdit } from "@v1/features/experiments/tabs/hooks/useExperimentEdit";
import { useOverridableInput } from "@v1/hooks/useOverridableInput";
import { useCurrentWorkspaceId } from "@v2/contexts/WorkspaceDashboardContext";
import { AddAnalyticsCustomDomainModal } from "@v2/features/settings/shop/modals/AddAnalyticsCustomDomainModal";
import { useSubscriptionInfo } from "@v2/hooks/subscription";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import {
  analyticsLinkRouter,
  analyticsLinkUtils,
} from "@v2/trpc/routes/analyticsLink";
import { workspaceRouter, workspaceUtils } from "@v2/trpc/routes/workspace";
import copy from "copy-to-clipboard";
import {
  ChevronDown,
  Copy,
  ExternalLink,
  Info,
  Lock,
  Search,
} from "lucide-react";
import { useForm } from "react-hook-form";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { EDGESERVER_DOMAIN_PROD } from "schemas/edgeserver";
import { isPathSafeSlug } from "schemas/utils";
import * as z from "zod";

type SubSectionProps = {
  title: string;
  className?: string;
  value: string;
  options?: (ComboboxOption & { displayValue: string })[];
  helpTooltipText: string;
  error?: string;
  setValue: (value: string) => void;
  onBlur?: (value: string) => void;
  onEnter?: (value: string) => void;
} & (
  | {
      type: "domain";
      openAddCustomDomainModal: () => void;
    }
  | {
      type: "shortName";
    }
  | { type: "slug" }
);

const SubSection: React.FC<SubSectionProps> = ({
  title,
  className,
  value,
  options: optionsFromProps,
  helpTooltipText,
  error,
  setValue,
  onBlur,
  onEnter,
  ...props
}) => {
  const { value: localValue, onChange: handleChange } = useOverridableInput({
    value,
    onValueChange: setValue,
  });

  const [input, setInput, options] = useCreatableComboboxInputOptions(
    optionsFromProps ?? [],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && onEnter) {
      onEnter(localValue);
    }
  };

  const [isComboboxOpen, setIsComboboxOpen] = React.useState(false);

  const inputComponent = exhaustiveSwitch(props)({
    shortName: () => (
      <Combobox.Root
        input={input}
        onInputChange={setInput}
        options={options ?? []}
        value={value}
        onChange={setValue}
        open={isComboboxOpen}
        onOpenChange={setIsComboboxOpen}
      >
        <Combobox.Trigger>
          <Combobox.Input
            placeholder="Select Group Name"
            startEnhancer={<Search className="h-3 w-3" />}
            outlineOnActive={false}
          />
        </Combobox.Trigger>
        <Combobox.Popover>
          <Combobox.Content />
        </Combobox.Popover>
      </Combobox.Root>
    ),
    domain: ({ openAddCustomDomainModal }) => {
      const selectedOption = optionsFromProps?.find(
        (option) => option.value === value,
      );
      return (
        <Combobox.Root
          options={options ?? []}
          value={value}
          onChange={setValue}
          open={isComboboxOpen}
          onOpenChange={setIsComboboxOpen}
        >
          <Combobox.Trigger>
            <Combobox.SelectionButton
              title={selectedOption?.displayValue ?? "Select Domain"}
              isPlaceholder={!Boolean(selectedOption)}
            />
          </Combobox.Trigger>
          <Combobox.Popover>
            <Combobox.Content />
            <Combobox.Footer size="sm">
              <DeprecatedButton
                variant="link"
                size="sm"
                onClick={() => {
                  setIsComboboxOpen(false);
                  openAddCustomDomainModal();
                }}
              >
                Add Custom Domain
              </DeprecatedButton>
            </Combobox.Footer>
          </Combobox.Popover>
        </Combobox.Root>
      );
    },
    slug: () => (
      <Input
        size="base"
        placeholder="Enter Slug"
        value={localValue}
        onChange={handleChange}
        onBlur={() => onBlur?.(localValue)}
        onKeyDown={handleKeyDown}
        unsafe_inputClassName="text-xs"
      />
    ),
  });

  return (
    <div className={twMerge(className, "flex flex-col gap-1")}>
      <div className="flex flex-row items-center gap-1">
        <span className="text-sm font-medium ">{title}</span>
        <Tooltip
          content={helpTooltipText}
          side="top"
          delay={300}
          triggerAsChild
        >
          <div>
            <Info size={12} />
          </div>
        </Tooltip>
      </div>
      {inputComponent}
      {error && <span className="text-rose mt-1 text-xs">{error}</span>}
    </div>
  );
};

type DomainOrShortName = {
  value: string;
  id: string;
};

type CreateNewLinkProps = {
  experimentName: Experiment["name"];
  handleLinkSubsectionChange: (value: string) => void;
  shouldForceCreateNewLink: boolean;
  links: ConstructedAnalyticsLink[];
  customDomains: DomainOrShortName[];
  shortNames: DomainOrShortName[];
};

const createNewLinkSchema = (links: ConstructedAnalyticsLink[]) => {
  return z
    .object({
      customDomain: z.string(),
      shortName: z.string().nullable(),
      path: z.string().min(1, "Slug is required").refine(isPathSafeSlug, {
        message: "Slug must contain only letters, numbers, or hyphens",
      }),
    })
    .superRefine((data, ctx) => {
      if (!data.customDomain && !data.shortName) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Either a custom domain or short name must be provided",
          path: ["_form"],
        });
      }

      if (data.customDomain === EDGESERVER_DOMAIN_PROD && !data.shortName) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Short name is required when using the default domain",
          path: ["_form"],
        });
      }

      if (data.customDomain !== EDGESERVER_DOMAIN_PROD && data.shortName) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "Short name should not be provided when using a custom domain",
          path: ["_form"],
        });
      }

      const doesLinkAlreadyExist = links.some((link) => {
        const { customDomainValue, shortNameValue, path } = link;

        return (
          customDomainValue ===
            (data.customDomain === EDGESERVER_DOMAIN_PROD
              ? null
              : data.customDomain) &&
          shortNameValue === data.shortName &&
          path === data.path
        );
      });

      if (doesLinkAlreadyExist) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "This combination of domain, group name and slug is already used by another link.",
          path: ["_form"],
        });
      }
    });
};

type AnalyticsLinkFormData = z.infer<ReturnType<typeof createNewLinkSchema>> & {
  _form?: string;
};

const CreateNewLink: React.FC<
  Omit<CreateNewLinkProps, "customDomains" | "shortNames">
> = (props) => {
  const workspaceId = useCurrentWorkspaceId() ?? undefined;
  const projectId = useCurrentProjectId();

  const { data: customDomains, isLoading: isLoadingCustomDomains } =
    workspaceRouter.getV1CustomDomains.useQuery(
      workspaceId || projectId ? { workspaceId, projectId } : skipToken,
    );

  const { data: shortNames, isLoading: isLoadingShortNames } =
    workspaceRouter.getShortNames.useQuery(
      workspaceId || projectId ? { workspaceId, projectId } : skipToken,
    );

  if (
    isLoadingCustomDomains ||
    isLoadingShortNames ||
    !customDomains ||
    !shortNames
  ) {
    return <Spinner className="size-[25px]" variant="primary" />;
  }

  return (
    <LoadedCreateNewLink
      customDomains={customDomains}
      shortNames={shortNames}
      workspaceId={workspaceId}
      projectId={projectId}
      {...props}
    />
  );
};

const LoadedCreateNewLink: React.FC<
  CreateNewLinkProps & {
    workspaceId: string | undefined;
    projectId: string | undefined;
  }
> = ({
  experimentName,
  handleLinkSubsectionChange,
  shouldForceCreateNewLink,
  links,
  customDomains,
  shortNames,
  workspaceId,
  projectId,
}) => {
  const {
    mutateAsync: createAnalyticsLinkAsync,
    isPending: isCreatingAnalyticsLink,
  } = analyticsLinkRouter.create.useMutation({});

  const { dispatchExperimentEdit } = useExperimentEdit();

  const domainOptions = [
    {
      value: EDGESERVER_DOMAIN_PROD,
      label: EDGESERVER_DOMAIN_PROD,
      displayValue: EDGESERVER_DOMAIN_PROD,
    },
    ...(customDomains ?? []).map((domain) => ({
      value: domain.value,
      label: domain.value,
      displayValue: domain.value,
    })),
  ];

  const shortNameOptions = (shortNames ?? []).map((shortName) => ({
    value: shortName.value,
    label: shortName.value,
    displayValue: shortName.value,
  }));

  const customDomainOptions = domainOptions.filter(
    (option) => option.value !== EDGESERVER_DOMAIN_PROD,
  );

  const domainOptionsValue =
    customDomainOptions[0]?.value ?? EDGESERVER_DOMAIN_PROD;

  const defaultAnalyticsLinkCreate: AnalyticsLinkFormData = {
    customDomain: domainOptionsValue,
    shortName:
      domainOptionsValue !== EDGESERVER_DOMAIN_PROD
        ? null
        : shortNameOptions[0]?.value ?? null,
    path: experimentName,
  };

  const {
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: { errors, isValid },
  } = useForm<AnalyticsLinkFormData>({
    resolver: zodResolver(createNewLinkSchema(links)),
    defaultValues: defaultAnalyticsLinkCreate,
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const formLevelError = errors._form?.message;

  const handleDomainChange = (value: string) => {
    setValue("customDomain", value);
    if (value !== EDGESERVER_DOMAIN_PROD) {
      setValue("shortName", null);
    } else {
      setValue("shortName", shortNameOptions[0]?.value ?? null);
    }
    void trigger();
  };

  const handleShortNameChange = (value: string) => {
    setValue("shortName", value);
    void trigger();
  };

  const handleSlugChange = (value: string) => {
    setValue("path", value);
    void trigger();
  };

  const onSubmit = async (data: AnalyticsLinkFormData) => {
    if (workspaceId || projectId) {
      const analyticsLink = {
        ...data,
        customDomain:
          data.customDomain === EDGESERVER_DOMAIN_PROD
            ? null
            : data.customDomain,
      };

      const result = await createAnalyticsLinkAsync({
        workspaceId,
        projectId,
        analyticsLink,
      });
      await analyticsLinkUtils.list.invalidate({
        workspaceId,
        projectId,
      });
      await workspaceUtils.getShortNames.invalidate({
        workspaceId,
        projectId,
      });

      handleLinkSubsectionChange("chooseLink");
      successToast("Experiment Link Created", "");
      dispatchExperimentEdit({
        type: "changeProperty",
        payload: { key: "analyticsLinkId", value: result.analyticsLinkId },
      });
    }
  };

  const customDomain = watch("customDomain");
  const isShortNameFieldRequired =
    !customDomain || customDomain === EDGESERVER_DOMAIN_PROD;

  const handleSubmission = handleSubmit(onSubmit);
  const onCreateLink = () => {
    void handleSubmission();
  };

  const [customDomainModalOpen, setCustomDomainModalOpen] =
    React.useState(false);

  const openAddCustomDomainModal = () => {
    setCustomDomainModalOpen(true);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-12 gap-3">
        <SubSection
          title="Domain"
          className="col-span-4"
          type="domain"
          value={customDomain}
          options={domainOptions}
          setValue={handleDomainChange}
          helpTooltipText="Use our default reploedge domain, or use your own custom domain"
          error={errors.customDomain?.message}
          openAddCustomDomainModal={openAddCustomDomainModal}
        />
        {isShortNameFieldRequired && (
          <SubSection
            title="Group Name"
            className="col-span-4"
            type="shortName"
            value={watch("shortName") ?? ""}
            options={shortNameOptions}
            setValue={handleShortNameChange}
            helpTooltipText="A unique name identifying your workspace, to distinguish it from others in the Replo system. Only required when using reploedge.com as your domain"
            error={errors.shortName?.message}
          />
        )}
        <SubSection
          title="Slug"
          className="col-span-4"
          type="slug"
          value={watch("path") ?? ""}
          setValue={handleSlugChange}
          helpTooltipText="The URL path that distinguishes this link from the other links in your workspace"
          error={errors.path?.message}
        />
      </div>
      <AddAnalyticsCustomDomainModal
        open={customDomainModalOpen}
        onOpenChange={setCustomDomainModalOpen}
        workspaceId={workspaceId ?? null}
        projectId={projectId ?? null}
      />
      {formLevelError && (
        <span className="text-rose mt-1 text-xs">{formLevelError}</span>
      )}
      <div className="flex flex-row justify-between">
        <Expandable.Trigger>
          <span className="text-xs font-medium">What's this?</span>
        </Expandable.Trigger>
        <div className="flex flex-row justify-end gap-2">
          {!shouldForceCreateNewLink && (
            <DeprecatedButton
              variant="secondary"
              size="sm"
              onClick={() => handleLinkSubsectionChange("chooseLink")}
            >
              Cancel
            </DeprecatedButton>
          )}

          <DeprecatedButton
            variant="primary"
            size="sm"
            onClick={onCreateLink}
            isLoading={isCreatingAnalyticsLink}
            disabled={!isValid}
          >
            Create Link
          </DeprecatedButton>
        </div>
      </div>
    </div>
  );
};

const ChooseLinkOption: React.FC<{
  url: string;
  isActive: boolean;
}> = ({ url, isActive }) => {
  return (
    <MenuItem variant="default" size="sm">
      <div className="flex flex-row items-center gap-2">
        <span className="flex-1 truncate">{url}</span>
        {isActive && <span className="bg-emerald h-1 w-1 rounded-full" />}
      </div>
    </MenuItem>
  );
};

type ChooseLinkProps = {
  analyticsLinkId: string | null;
  handleChooseLinkChange: (analyticsLinkId: string) => void;
  handleLinkSubsectionChange: (value: string) => void;
  links: ConstructedAnalyticsLink[];
  isEditable?: boolean;
};
const ChooseLink: React.FC<ChooseLinkProps> = ({
  analyticsLinkId,
  handleChooseLinkChange,
  handleLinkSubsectionChange,
  links,
  isEditable = true,
}) => {
  const handleOptionClick = (analyticsLinkId: string) => {
    handleChooseLinkChange(analyticsLinkId);
  };

  const options =
    links.map((constructedLink) => {
      const { id, url, isActive } = constructedLink;
      return {
        value: id,
        label: <ChooseLinkOption url={url} isActive={isActive} />,
        isDisabled: isActive,
        displayValue: url,
      };
    }) ?? [];
  const selectedOption = options.find(
    (option) => option.value === analyticsLinkId,
  );
  return (
    <Combobox
      onChange={handleOptionClick}
      options={options}
      trigger={
        <Combobox.SelectionButton
          title={
            selectedOption?.displayValue ?? "Choose from your existing links"
          }
          endEnhancer={
            isEditable ? (
              <ChevronDown className="ml-auto" size={12} />
            ) : (
              <Lock className="ml-auto" size={12} />
            )
          }
          isDisabled={!isEditable}
          layoutClassName="w-full"
        />
      }
      value={analyticsLinkId ?? undefined}
      footer={
        <Combobox.Footer size="sm">
          <DeprecatedButton
            variant="link"
            size="sm"
            onClick={() => handleLinkSubsectionChange("createNewLink")}
          >
            Create New Link
          </DeprecatedButton>
        </Combobox.Footer>
      }
    />
  );
};

const LinkInfographicContent: React.FC = () => {
  return (
    <div className="border-0.5 border-border bg-surface mt-3 flex items-center justify-center rounded-md p-4 pb-0">
      <img
        src="/images/experiments/education/what-is-a-link.svg"
        alt="Link Infographic"
      />
    </div>
  );
};

type LinkSectionProps = {
  isEditable?: boolean;
  linkSubSection: "chooseLink" | "createNewLink";
  handleLinkSubsectionChange: (value: string) => void;
  links: ConstructedAnalyticsLink[];
  experiment: {
    analyticsLinkId: Experiment["analyticsLinkId"];
    name: Experiment["name"];
  };
  shouldForceCreateNewLink: boolean;
};

export const LinkSection: React.FC<LinkSectionProps> = ({
  isEditable = true,
  linkSubSection,
  handleLinkSubsectionChange,
  links,
  experiment,
  shouldForceCreateNewLink,
}) => {
  const logEvent = useLogAnalytics();
  const { subscriptionInfo } = useSubscriptionInfo();
  const subscriptionTier = subscriptionInfo?.tier || "free";

  const { dispatchExperimentEdit } = useExperimentEdit();

  const handleChooseLinkChange = (analyticsLinkId: string) => {
    dispatchExperimentEdit({
      type: "changeProperty",
      payload: { key: "analyticsLinkId", value: analyticsLinkId },
    });
  };

  const getSelectedLinkUrl = (): string | undefined => {
    if (!links || !experiment.analyticsLinkId) {
      return undefined;
    }

    const selectedLink = links.find(
      (link) => link.id === experiment.analyticsLinkId,
    );

    return selectedLink?.url;
  };

  return (
    <DetailsContainer title="Test Link" isRequired>
      <Expandable.Root>
        <div className="border-0.5 border-border flex flex-col rounded-sm p-4">
          <div className="flex flex-col gap-3">
            {linkSubSection === "chooseLink" ? (
              <ChooseLink
                analyticsLinkId={experiment.analyticsLinkId}
                handleChooseLinkChange={handleChooseLinkChange}
                handleLinkSubsectionChange={handleLinkSubsectionChange}
                links={links ?? []}
                isEditable={isEditable}
              />
            ) : (
              <CreateNewLink
                experimentName={experiment.name}
                handleLinkSubsectionChange={handleLinkSubsectionChange}
                shouldForceCreateNewLink={shouldForceCreateNewLink}
                links={links ?? []}
              />
            )}
            {linkSubSection === "chooseLink" && (
              <div className="flex flex-row justify-between">
                <Expandable.Trigger>
                  <span className="text-xs font-medium">What's this?</span>
                </Expandable.Trigger>
                <div className="flex flex-row justify-end">
                  <div className="flex flex-row gap-2">
                    {experiment.analyticsLinkId && (
                      <DeprecatedButton
                        variant="tertiary"
                        endEnhancer={<Copy size={12} />}
                        size="sm"
                        onClick={() => {
                          const url = getSelectedLinkUrl();
                          if (url) {
                            copy(url);
                            logEvent("experiment.link.copy", {
                              billingPlanTier: subscriptionTier,
                            });
                          }
                        }}
                      >
                        Copy Link
                      </DeprecatedButton>
                    )}

                    {!isEditable && (
                      <DeprecatedButton
                        variant="secondary"
                        endEnhancer={<ExternalLink size={12} />}
                        size="sm"
                        to={getSelectedLinkUrl() ?? ""}
                        onClick={() => {
                          logEvent("experiment.link.preview", {
                            billingPlanTier: subscriptionTier,
                          });
                        }}
                      >
                        Preview
                      </DeprecatedButton>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
          <Expandable.Content>
            <LinkInfographicContent />
          </Expandable.Content>
        </div>
      </Expandable.Root>
    </DetailsContainer>
  );
};
